import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import FeatherIcon from "feather-icons-react";
import useAxios from "../../../utils/useAxios";
import {useHistory, useLocation, useParams} from "react-router-dom";
import { renderScore } from "../../../components/renderScore";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import {formatLastName} from "../../../constants";
import ParticipantActionsModal from "../../../components/ParticipantActionsModal";
import { chipsMatchEntity, searchStateClass } from "../../../utils/searchMatch";
import { fetchTeamRoster } from "../../../utils/fetchTeamRoster";

function Bracket({
                                    draw,
                                    tournamentGrade,
                                    setModalOpen,
                                    setModalData,
                                    pickerTarget,
                                    pickedCandidate,
                                    onPlaceCandidate,
                                    onCancelCandidate,
                                    mainGridNumber,
                                    onDeleteParticipant,
                                    expandMatch,
                                    is_authorized,
                                    canEdit,
                                    updateMatches,
                                    onMoveFailed,
                                    searchChips = [],
                                    teamRoster,
                                    playerNames,
                                    loadingCells
                                }) {
    const N = useMemo(() => Math.pow(2, Math.ceil(Math.log2(Math.max(2, draw.number_players)))), [draw.number_players]);
    const R = useMemo(() => Math.log2(N), [N]);
    const isMainDraw = draw.number === mainGridNumber;
    const api = useAxios();
    const { id } = useParams();
    const { t, i18n } = useTranslation();
    const [actionsModal, setActionsModal] = useState(null);
    const [pickedSlot, setPickedSlot] = useState(null);
    const [isTouchDevice, setIsTouchDevice] = useState(false);
    // slots currently mid-move (keyed by "matchId_side") - the optimistic update in
    // movePlayer makes the swap itself feel instant, but on heavier brackets the
    // surrounding re-render can still lag; this drives a small loader (same look as
    // MatchStatusSelect's status-loader) on the two affected slots as a fallback
    // so a slow paint doesn't read as a dead tap/drop
    const [loadingSlots, setLoadingSlots] = useState({});
    const longPressTimer = useRef(null);

    useEffect(() => {
        const onTouch = () => setIsTouchDevice(true);
        window.addEventListener("touchstart", onTouch, { once: true });
        return () => window.removeEventListener("touchstart", onTouch);
    }, []);

    // ── Лукапы: один проход по массиву ────────────────────────────
    const { round1Matches, matchByNum } = useMemo(() => {
        const byNum = {};
        const r1 = [];
        (draw.matches || []).forEach(m => {
            byNum[m.match_number] = m;
            if (m.round_number === 1) r1.push(m);
        });
        r1.sort((a, b) => a.match_number - b.match_number);
        return { round1Matches: r1, matchByNum: byNum };
    }, [draw.matches]);

    const matchOffset = useMemo(() => {
        const nums = (draw.matches || []).map(m => m.match_number);
        return nums.length ? Math.min(...nums) - 1 : 0;
    }, [draw.matches]);

    const matchById = useMemo(() => {
        const byId = {};
        (draw.matches || []).forEach(m => { byId[m.id] = m; });
        return byId;
    }, [draw.matches]);

    const matchStart = useCallback(r => matchOffset + N - (N >> (r - 1)), [N, matchOffset]);

    // ── ЛОГИКА ОТОБРАЖЕНИЯ ПОСЕВА ─────────────────────────────────
    const showSeedColumn = useMemo(() => {
        if (!isMainDraw) return false;
        if (is_authorized) return true;
        return (draw.matches || []).some(m => m.seed1 || m.seed2);
    }, [isMainDraw, is_authorized, draw.matches]);

    const roundLabel = useCallback((r) => {
        if (r === R - 1) return t("Final");
        if (r === R) return t("winner_of_players", { count: draw.number });

        if (i18n.language === "en") {
            if (r === R - 2) return "SF";
            if (r === R - 3) return "QF";
            return `R${r + 1}`;
        }

        return `1/${2 ** (R - r - 1)}`;
    }, [R, t, i18n.language, draw.number]);

    const roundFirst = useCallback((R) => (
        R === 1 ? t("Final") :
            R === 2 ? (i18n.language === "en" ? "SF" : `1/2`) :
                R === 3 ? (i18n.language === "en" ? "QF" : `1/4`) :
                    (i18n.language === "en" ? "R1" : `1/${2 ** (R - 1)}`)
    ), [i18n.language]);

    const handleSeedChange = useCallback((matchId, side, value) => {
        const cleanValue = value.trim();
        const payloadValue = cleanValue === "" ? "" : cleanValue;
        api.patch(`/api/v1.0/tournaments/${id}/draws/matches/${matchId}/`, {
            [side === 1 ? "seed1" : "seed2"]: payloadValue
        }).then().catch(err => {
            console.error("Error:", err);
        });
    }, [api, id]);

    const handlePlayerClick = useCallback((match, side) => {
        if (!isMainDraw || match.round_number !== 1) return;
        setModalData({ matchId: match.id, side, isAlreadyInMatch: match.x1 || match.x2 });
        setModalOpen(true);
    }, [isMainDraw, setModalData, setModalOpen]);

    const handleMatchClick = useCallback((matchNum) => {
        const m = matchByNum[matchNum];
        if (m) expandMatch(m.match_number);
    }, [matchByNum, expandMatch]);

    const hasParticipant = useCallback((match, side) => {
        if (side === 1 ? match.x1 : match.x2) return true;
        if (tournamentGrade === 1) return !!(side === 1 ? match.player_1_name : match.player_2_name);
        if (tournamentGrade === 2) {
            const p1 = side === 1 ? match.player_1_name : match.player_2_name;
            const p2 = side === 1 ? match.player_3_name : match.player_4_name;
            return !!(p1 && p2);
        }
        return !!(side === 1 ? match.team_1_name || match.team_1 : match.team_2_name || match.team_2);
    }, [tournamentGrade]);

    const extractSlotData = useCallback((match, side) => {
        const isBye = side === 1 ? match.x1 : match.x2;
        if (isBye) return { isBye: true };
        if (tournamentGrade === 1) return {
            player: side === 1 ? match.player_1 : match.player_2,
            player_name: side === 1 ? match.player_1_name : match.player_2_name
        };
        if (tournamentGrade === 2) return {
            p1: side === 1 ? match.player_1 : match.player_2,
            p2: side === 1 ? match.player_3 : match.player_4,
            p1_name: side === 1 ? match.player_1_name : match.player_2_name,
            p2_name: side === 1 ? match.player_3_name : match.player_4_name
        };
        return {
            team: side === 1 ? match.team_1 : match.team_2,
            team_name: side === 1 ? match.team_1_name : match.team_2_name,
            team_number: side === 1 ? match.team_1_number : match.team_2_number
        };
    }, [tournamentGrade]);

    const buildSlotPayload = useCallback((data, side) => {
        if (data.isBye) return side === 1 ? { x1: true } : { x2: true };
        if (tournamentGrade === 1) return side === 1 ? { player_1: data.player } : { player_2: data.player };
        if (tournamentGrade === 2) return side === 1
            ? { player_1: data.p1, player_3: data.p2 }
            : { player_2: data.p1, player_4: data.p2 };
        return side === 1 ? { team_1: data.team } : { team_2: data.team };
    }, [tournamentGrade]);

    // like buildSlotPayload, but also carries the *_name fields so the move
    // can be reflected in local state immediately (see movePlayer) instead of
    // waiting on the round trip - the API payload itself only needs the ids
    const buildOptimisticSlotPayload = useCallback((data, side) => {
        if (data.isBye) return side === 1 ? { x1: true } : { x2: true };
        if (tournamentGrade === 1) return side === 1
            ? { player_1: data.player, player_1_name: data.player_name, x1: null }
            : { player_2: data.player, player_2_name: data.player_name, x2: null };
        if (tournamentGrade === 2) return side === 1
            ? { player_1: data.p1, player_3: data.p2, player_1_name: data.p1_name, player_3_name: data.p2_name, x1: null }
            : { player_2: data.p1, player_4: data.p2, player_2_name: data.p1_name, player_4_name: data.p2_name, x2: null };
        return side === 1
            ? { team_1: data.team, team_1_name: data.team_name, team_1_number: data.team_number, x1: null }
            : { team_2: data.team, team_2_name: data.team_name, team_2_number: data.team_number, x2: null };
    }, [tournamentGrade]);

    const clearSlotPayload = useCallback((side) => {
        if (tournamentGrade === 1) return side === 1
            ? { player_1: null, player_1_name: null, x1: null }
            : { player_2: null, player_2_name: null, x2: null };
        if (tournamentGrade === 2) return side === 1
            ? { player_1: null, player_3: null, player_1_name: null, player_3_name: null, x1: null }
            : { player_2: null, player_4: null, player_2_name: null, player_4_name: null, x2: null };
        return side === 1
            ? { team_1: null, team_1_name: null, team_1_number: null, x1: null }
            : { team_2: null, team_2_name: null, team_2_number: null, x2: null };
    }, [tournamentGrade]);

    const slotKey = (matchId, side) => `${matchId}_${side}`;

    const movePlayer = useCallback(async (src, target) => {
        if (src.matchId === target.matchId && src.side === target.side) return;
        const srcMatch = matchById[src.matchId];
        const targetMatch = matchById[target.matchId];
        if (!srcMatch || !targetMatch) return;

        const srcData = extractSlotData(srcMatch, src.side);
        const targetOccupied = hasParticipant(targetMatch, target.side);
        const targetData = targetOccupied ? extractSlotData(targetMatch, target.side) : null;

        const srcKey = slotKey(src.matchId, src.side);
        const targetKey = slotKey(target.matchId, target.side);
        setLoadingSlots(prev => ({ ...prev, [srcKey]: true, [targetKey]: true }));

        // optimistic update: reflect the swap in local state right away so it
        // reads as instant on tap/drop, same as the long-press flow in groups
        // (TournamentGroups.movePlayer) - reconciled below with the API's
        // authoritative response, and rolled back via onMoveFailed on error
        updateMatches([
            { id: target.matchId, ...buildOptimisticSlotPayload(srcData, target.side) },
            { id: src.matchId, ...(targetOccupied ? buildOptimisticSlotPayload(targetData, src.side) : clearSlotPayload(src.side)) }
        ], false);

        try {
            if (targetOccupied) {
                const r1 = await api.patch(`/api/v1.0/tournaments/${id}/draws/matches/${src.matchId}/`, buildSlotPayload(targetData, src.side));
                // fetch_pl=false: this is a swap between two already-placed slots in
                // the same draw, the tournament's participant pool didn't change.
                // Doing it on every intermediate update (previously the default on
                // both calls) fires an extra fetchParticipants() network round-trip
                // per step, which is what made repeated long-press moves feel like
                // the page was hanging - one refetch after the whole swap is enough
                updateMatches(r1.data.updated_matches, false);
                const r2 = await api.patch(`/api/v1.0/tournaments/${id}/draws/matches/${target.matchId}/`, buildSlotPayload(srcData, target.side));
                updateMatches(r2.data.updated_matches);
            } else {
                const r1 = await api.patch(`/api/v1.0/tournaments/${id}/draws/matches/${target.matchId}/`, buildSlotPayload(srcData, target.side));
                updateMatches(r1.data.updated_matches, false);
                const r2 = await api.delete(`/api/v1.0/tournaments/${id}/draws/matches/${src.matchId}/?side=${src.side}`);
                if (r2?.data?.updated_matches) updateMatches(r2.data.updated_matches);
            }
        } catch (e) {
            console.error("Failed to move participant:", e);
            onMoveFailed?.();
        } finally {
            setLoadingSlots(prev => {
                const next = { ...prev };
                delete next[srcKey];
                delete next[targetKey];
                return next;
            });
        }
    }, [matchById, extractSlotData, buildSlotPayload, buildOptimisticSlotPayload, clearSlotPayload, api, id, updateMatches, hasParticipant, onMoveFailed]);

    const handleCellTap = useCallback((matchId, side) => {
        if (!pickedSlot) return;

        const src = pickedSlot;
        setPickedSlot(null);

        // tapping the source slot again cancels the pick instead of moving it
        if (src.matchId === matchId && src.side === side) return;
        movePlayer(src, { matchId, side });
    }, [pickedSlot, movePlayer]);

    const handleDragStart = useCallback((e, matchId, side) => {
        e.dataTransfer.setData("matchId", matchId);
        e.dataTransfer.setData("side", String(side));
        e.dataTransfer.effectAllowed = "move";
    }, []);

    const handleDrop = useCallback((e, matchId, side) => {
        e.preventDefault();
        const srcMatchId = e.dataTransfer.getData("matchId");
        const srcSide = Number(e.dataTransfer.getData("side"));
        if (!srcMatchId) return;
        movePlayer({ matchId: srcMatchId, side: srcSide }, { matchId, side });
    }, [movePlayer]);

    const renderSource = useCallback((match, side) => {
        const src = side === 1 ? match.source_match_1 : match.source_match_2;
        if (!src) return null;
        const label = match.is_source_winner ? t("Winner") : t("Loser");
        const className = match.is_source_winner ? "winner_color" : "loser_color";

        return (
            <span className={className}>{label} #{src}</span>
        );
    }, [t]);


    const history = useHistory();
    const location = useLocation();

    const handlePlClick = (pId, history, location) => {
        const params = new URLSearchParams(location.search);
        params.set("player_id", pId);
        // replace, not push - a push here left an extra history entry that
        // the tournament header's own back-arrow goBack() would undo first
        // (see TournamentHeader.js's arrivalActionRef comment for the same
        // bug already fixed once for tab switches), so pressing back after
        // opening a player's profile just closed the profile instead of
        // actually leaving the tournament.
        history.replace({ search: params.toString() });
    };

    const buildProfile = useCallback((p, pid) => ({
        icon: "user",
        label: `${p?.first_name ? p.first_name[0] + '. ' : ''}${formatLastName(p?.last_name)}`,
        onClick: () => handlePlClick(pid, history, location)
    }), [history, location]);

    const buildDeleteAction = useCallback((match, side) => ({
        icon: "trash-2",
        label: t("Delete"),
        danger: true,
        onClick: async () => {
            try {
                const response = await api.delete(`/api/v1.0/tournaments/${id}/draws/matches/${match.id}/?side=${side}`);
                onDeleteParticipant(response);
            } catch (e) {
                console.error(e);
                onDeleteParticipant(null);
            }
        }
    }), [api, id, t, onDeleteParticipant]);

    // "Who's in this slot" menu - open to every viewer (not just admins editing
    // the main draw), same as BracketClassic's own openParticipantMenu. Works
    // for any round's match/side, not just round 1 - unlike round-1 initial
    // slots, later-round cells only ever show a name here (score/match-number
    // live in their own separate columns), so there's no name-vs-rest split to
    // do - the whole cell just opens this menu instead of the match, and the
    // match stays reachable via those other columns.
    // allowMove: only passed true when opened (via click) on an editable,
    // occupied main-draw round-1 slot - prepends a "Move" entry that hands
    // off into the exact same pickedSlot drag flow the long-press gesture
    // (handleTouchStart below) already starts directly - offered here too
    // so it doesn't require knowing that gesture, same as Groups' own menu.
    // Never offered on a placement draw/later round with no move of its own.
    const openParticipantMenu = useCallback(async (match, side, { allowMove = false } = {}) => {
        if (!match) return;
        const deleteAction = is_authorized ? [buildDeleteAction(match, side)] : [];
        const moveAction = allowMove ? [{
            icon: "move",
            label: t("Move"),
            onClick: () => setPickedSlot({ matchId: match.id, side })
        }] : [];
        // Offered whenever both sides are filled (same condition bothPlayersExist
        // uses to let a plain click on the rest of the cell open the match) - on
        // a 2-slot bracket the round-1 match is also the whole draw's only
        // match, and its slots are editable, so a plain click on the name never
        // reaches the cell's own open-match click handler (it's swallowed by
        // this very menu below instead) - there was previously no way at all to
        // reach that match from here.
        const matchAction = hasParticipant(match, 1) && hasParticipant(match, 2) ? [{
            iconText: "-:-",
            label: t("Match"),
            onClick: () => handleMatchClick(match.match_number)
        }] : [];

        // A Bye has no player/team name to build a title or profile action
        // from (the grade-specific branches below all bail on that) - it
        // still needs to be removable though, so handle it before them with
        // just the Delete action instead of falling through to their own
        // "no name, nothing to show" early returns.
        const isBye = side === 1 ? match.x1 : match.x2;
        if (isBye) {
            if (!deleteAction.length && !moveAction.length && !matchAction.length) return;
            setActionsModal({ title: t("Bye"), actions: [...matchAction, ...moveAction, ...deleteAction] });
            return;
        }

        if (tournamentGrade === 1) {
            const p = side === 1 ? match.player_1_name : match.player_2_name;
            const pid = side === 1 ? match.player_1 : match.player_2;
            if (!p || typeof p === "string") return;
            const title = `${p.first_name || ""} ${formatLastName(p.last_name)}`.trim();
            setActionsModal({ title, actions: [...matchAction, buildProfile(p, pid), ...moveAction, ...deleteAction] });
            return;
        }

        if (tournamentGrade === 2) {
            const p1 = side === 1 ? match.player_1_name : match.player_2_name;
            const p2 = side === 1 ? match.player_3_name : match.player_4_name;
            const id1 = side === 1 ? match.player_1 : match.player_2;
            const id2 = side === 1 ? match.player_3 : match.player_4;
            const title = [p1, p2].filter(Boolean).map(p => formatLastName(p.last_name)).join(" / ") || t("Participants");
            const profiles = [];
            if (p1) profiles.push(buildProfile(p1, id1));
            if (p2) profiles.push(buildProfile(p2, id2));
            setActionsModal({ title, actions: [...matchAction, ...profiles, ...moveAction, ...deleteAction] });
            return;
        }

        const teamId = side === 1 ? match.team_1 : match.team_2;
        const title = side === 1
            ? (match.team_1_name || (match.team_1 && `${t("Team")} ${match.team_1_number}`))
            : (match.team_2_name || (match.team_2 && `${t("Team")} ${match.team_2_number}`));
        setActionsModal({ title, actions: [{ icon: "loader", label: t("Loading..."), onClick: () => {} }] });
        const roster = await fetchTeamRoster(api, id, typeof teamId === "object" ? teamId?.id : teamId);
        setActionsModal({ title, actions: [...matchAction, ...roster.map(p => buildProfile(p, p.player_id || p.id)), ...moveAction, ...deleteAction] });
    }, [tournamentGrade, is_authorized, buildProfile, buildDeleteAction, hasParticipant, handleMatchClick, api, id, t]);

    // Long-press picks the slot up for a move directly, same as Groups' own
    // handlePlayerTouchStart - the menu (with its own "Move" entry, see
    // openParticipantMenu's allowMove param) is reached from a plain click
    // instead, not this gesture.
    const handleTouchStart = useCallback((e, matchId, side) => {
        if (!isTouchDevice) setIsTouchDevice(true);
        if (pickedSlot) return;
        // Can't start moving an already-placed participant while the
        // add-picker panel is docked open targeting a slot - see the same
        // guard in TournamentGroups.js.
        if (pickerTarget) return;
        // ...or while a candidate is picked from the panel, waiting for a
        // destination tap - same reasoning, different picked-state.
        if (pickedCandidate) return;

        clearTimeout(longPressTimer.current);
        const touch = e.touches[0];
        const startX = touch.clientX;
        const startY = touch.clientY;

        const timer = setTimeout(() => {
            if (navigator.vibrate) navigator.vibrate(50);
            setPickedSlot({ matchId, side });
        }, 400);

        longPressTimer.current = timer;
        const target = e.currentTarget;

        const checkMove = (eMove) => {
            const touchMove = eMove.touches[0];
            if (Math.abs(touchMove.clientX - startX) > 10 || Math.abs(touchMove.clientY - startY) > 10) {
                clearTimeout(longPressTimer.current);
            }
        };

        const cleanup = () => {
            clearTimeout(longPressTimer.current);
            target.removeEventListener("touchmove", checkMove);
            target.removeEventListener("touchend", cleanup);
            target.removeEventListener("touchcancel", cleanup);
        };

        target.addEventListener("touchmove", checkMove, { passive: true });
        target.addEventListener("touchend", cleanup, { once: true });
        target.addEventListener("touchcancel", cleanup, { once: true });
    }, [isTouchDevice, pickedSlot, pickerTarget, pickedCandidate]);

    // Every participant name belonging to a match (both sides, grade-aware) -
    // shared by renderSide's own per-side "matched" calls below and by the
    // match-number/score cells further down, so the whole match block (both
    // names, the #N label, the score) lights up/dims together as one unit
    // instead of just the name spans.
    // match.player_X_name only carries a single-letter first_name (display-only
    // backend quirk, same as the groups endpoint - see fetchPlayerNameMap.js's
    // own comment); resolve the full name from the prefetched playerNames map
    // (keyed by player id) so a full-first-name search chip still matches,
    // without touching the intentional initial shown in renderSide below.
    const resolvePlayer = useCallback((p, pid) => {
        const key = pid || p?.id;
        return (p && key) ? (playerNames?.get(key) || p) : p;
    }, [playerNames]);

    const getMatchNames = useCallback((match) => {
        if (tournamentGrade === 1) return [resolvePlayer(match.player_1_name, match.player_1), resolvePlayer(match.player_2_name, match.player_2)];
        if (tournamentGrade === 2) return [
            resolvePlayer(match.player_1_name, match.player_1), resolvePlayer(match.player_3_name, match.player_3),
            resolvePlayer(match.player_2_name, match.player_2), resolvePlayer(match.player_4_name, match.player_4)
        ];
        const team1 = match.team_1_name || (match.team_1 && `${t("Team")} ${match.team_1_number}`);
        const team2 = match.team_2_name || (match.team_2 && `${t("Team")} ${match.team_2_number}`);
        return [team1, ...(teamRoster?.get(team1) || []), team2, ...(teamRoster?.get(team2) || [])];
    }, [tournamentGrade, teamRoster, resolvePlayer, t]);

    const matchSearchState = useCallback((match) => (
        match ? chipsMatchEntity(searchChips, { names: getMatchNames(match), drawValue: draw.number }) : null
    ), [getMatchNames, searchChips, draw.number]);

    const renderSide = useCallback((match, side, isMainDraw=false, slotNumber=null, isPickerTarget=false) => {
        // a bye never gets its own winner_side (there's no match to record a
        // result on) - the side facing it still advances automatically, so
        // it reads as a win too, not the same "no result yet" blank as an
        // actually-unplayed match.
        const opponentIsBye = side === 1 ? match.x2 : match.x1;
        const isWinner = match.winner_side === side || opponentIsBye;
        const isLoser = match.winner_side !== null && match.winner_side !== side;
        // was loser-only - the winner never got a class at all, so it just sat
        // at the default text color instead of reading as the winner.
        const sideClass = isWinner ? "match-winner" : (isLoser ? "match-loser" : "");
        const wrap = (content, matched = null) => <span className={`${sideClass} ${searchStateClass(matched)}`}>{content}</span>;

        if (side === 1 ? match.x1 : match.x2) return wrap(<div style={{ color: "var(--secondary-color)" }}>{t("Bye")}</div>);
        if (tournamentGrade === 1) {
            const p = side === 1 ? match.player_1_name : match.player_2_name;
            // Both sides tested together (not just this one) so a match on
            // either participant highlights the whole match block - both
            // name slots light up together, not just whichever one matched.
            const matched = matchSearchState(match);
            if (!p) return (isMainDraw && match.round_number === 1)
                ? (
                        is_authorized
                            ? wrap(<span className={`group-add-participant ${isPickerTarget ? "picker-target" : ""}`}>+ {t("Participant")}</span>, matched)
                            : wrap(<span className="black-name">{t("Participant")}{slotNumber ? ` #${slotNumber}` : ""}</span>, matched)
                    )
                : wrap(renderSource(match, side), matched);
            return typeof p === "string"
                ? wrap(<span className="uppercase0">{formatLastName(p)}</span>, matched)
                : wrap(<div>
                    <span className="black-name">
                        {p?.first_name && <>{p.first_name[0]}. </>}<span className="uppercase0">{formatLastName(p.last_name)}</span>
                    </span>
                </div>, matched);
        }

        if (tournamentGrade === 2) {
            const p1 = side === 1 ? match.player_1_name : match.player_2_name;
            const p2 = side === 1 ? match.player_3_name : match.player_4_name;
            // Both sides' pairs tested together - see the grade-1 branch's
            // identical comment above.
            const matched = matchSearchState(match);
            if (!p1 || !p2) return (isMainDraw && match.round_number === 1)
            ? (
                is_authorized
                    ? wrap(<span className={`group-add-participant ${isPickerTarget ? "picker-target" : ""}`}>+ {t("Participant")}</span>, matched)
                    : wrap(<span className="black-name">{t("Participant")}{slotNumber ? ` #${slotNumber}` : ""}</span>, matched)
            )
                : wrap(renderSource(match, side), matched);
            const fmt = (p) => (
                <span className="black-name">
                    {p.first_name ? `${p.first_name[0]}. ` : " "}<span className="uppercase0">{formatLastName(p.last_name)}</span>
                </span>
            );
            return wrap(<>{fmt(p1)}<br />{fmt(p2)}</>, matched);
        }

        const team = side === 1
            ? match.team_1_name || (match.team_1 && `${t("Team")} ${match.team_1_number}`)
            : match.team_2_name || (match.team_2 && `${t("Team")} ${match.team_2_number}`);
        // Both teams tested together - see the grade-1 branch's identical
        // comment above.
        const matched = matchSearchState(match);
        if (!team) return (isMainDraw && match.round_number === 1)
            ? (
                is_authorized
                    ? wrap(<span className={`group-add-participant ${isPickerTarget ? "picker-target" : ""}`}>+ {t("Participant")}</span>, matched)
                    : wrap(<span className="black-name">{t("Participant")}{slotNumber ? ` #${slotNumber}` : ""}</span>, matched)
            )
            : wrap(renderSource(match, side), matched);
        return (is_authorized && isMainDraw && tournamentGrade === 3 && match.round_number === 1)
            ? wrap(<div className="black-name">{team}</div>, matched)
            : wrap(<span className="black-name">{team}</span>, matched);
    }, [tournamentGrade, isMainDraw, renderSource, t, is_authorized, matchSearchState]);

    const headerCells = useMemo(() => [
        <td key="n">№</td>,
        ...(showSeedColumn ? [<td key="seed" style={{ textAlign: "center", width: is_authorized ? "auto" : "25px", color: "var(--text-gray)" }}>{is_authorized ? t("Seed") : t("S")}</td>] : []),
        <td key="r1">{roundFirst(R)}</td>,
        ...Array.from({ length: R }, (_, idx) => {
            const r = idx + 1;
            return [<td key={`mh${r}`} />, <td key={`ph${r}`}>{roundLabel(r)}</td>];
        }).flat(),
        <td key="spacer" style={{ width: '100%' }}></td>
    ], [isMainDraw, R, roundLabel, draw.number_players, t, showSeedColumn, is_authorized, roundFirst]);

    const bodyRows = useMemo(() => Array.from({ length: N }, (_, i) => {
        const cells = [];
        const isBottom  = i % 2 === 1;
        const matchIdx  = i >> 1;
        const side      = i % 2 === 0 ? 1 : 2;
        const r1Match   = round1Matches[matchIdx];
        // const bgColor = Math.floor(i / 2) % 2 === 0 ? "var(--super-white)" : "var(--white)";

        // upcoming matches used to fall back to var(--match) for non-admin viewers,
        // which is the same color as the page background and made them invisible —
        // keep the same completed-match background admins see until we add a
        // dedicated (e.g. shadow-based) treatment for upcoming matches
        const superWhiteBgColor = "var(--completed-match)";

        const getRs = (startI, stdSpan) => {
            let s = stdSpan;
            for (let r = startI; r < startI + stdSpan; r++) {
                if (r % 2 === 1 && r !== N - 1) s++;
            }
            return s;
        };

        // Builds the absolutely-positioned shadow-card style shared by the seed and
        // player cells. `sides` marks which of the cell's 4 edges are "internal" -
        // shared with another merged card (the seed/player pair horizontally, the
        // isBottom top/bottom pair vertically) - vs. "external" (the block's own
        // true outer edge). Internal sides get inset:0 (flush, no gap) and no
        // corner rounding there; external sides get the usual 2px gap + rounded
        // corner plus a real 1px border (same var(--white-blue) as .tournament-card
        // and the modal window's own border) - unlike box-shadow, a border has a
        // crisp exact edge with no blur, so it can't bleed onto a flush neighbor
        // and show as a seam there. clip-path still trims the shadow itself at
        // internal sides too, since that part can still spread past its own box.
        const cardShadowStyle = (sides) => ({
            position: "absolute",
            top: sides.top ? 0 : 2,
            right: sides.right ? 0 : 2,
            bottom: sides.bottom ? 0 : 2,
            left: sides.left ? 0 : 2,
            backgroundColor: superWhiteBgColor,
            boxShadow: "var(--card-shadow)",
            borderTop: sides.top ? "none" : "1px solid var(--white-blue)",
            borderRight: sides.right ? "none" : "1px solid var(--white-blue)",
            borderBottom: sides.bottom ? "none" : "1px solid var(--white-blue)",
            borderLeft: sides.left ? "none" : "1px solid var(--white-blue)",
            borderRadius: `${!sides.top && !sides.left ? 4 : 0}px ${!sides.top && !sides.right ? 4 : 0}px ${!sides.bottom && !sides.right ? 4 : 0}px ${!sides.bottom && !sides.left ? 4 : 0}px`,
            clipPath: `inset(${sides.top ? 0 : -20}px ${sides.right ? 0 : -20}px ${sides.bottom ? 0 : -20}px ${sides.left ? 0 : -20}px)`,
            pointerEvents: "none"
        });

        cells.push(<td key="n" style={{ backgroundColor: "transparent", background: "transparent", borderTop: "none", borderBottom: (i % 2 === 0) ? "1px solid var(--line)" : "none", borderLeft: "none", borderRight: "none", textAlign: "center", paddingLeft: "12px", paddingRight: "12px" }}>
            <label className={i % 2 === 0 ? "number-bottom" : "number-top"} style={{ marginTop: i % 2 === 1 ? "-8px" : undefined, display: "block" }}>{i + 1}</label>
        </td>);

        if (showSeedColumn) {
            // same internal/external split as cardShadowStyle's own sides - the
            // content has to sit inside the same inset box the shadow card
            // draws, or it pokes out past the card's border on the external
            // edge(s) instead of stopping at it.
            const seedSides = { top: isBottom, right: true, bottom: !isBottom, left: false };
            cells.push(
                <td key="seed" style={{
                    position: "relative",
                    textAlign: "center",
                    verticalAlign: "middle",
                    padding: 0,
                    border: "none",
                    width: is_authorized ? "50px" : "25px",
                    // real divider between this match's two players (top/bottom pair) -
                    // deliberately a border, not part of the shadow card, so it stays a
                    // crisp line instead of the faint blur clip-path leaves at that seam
                    borderBottom: isBottom ? "none" : "1px solid var(--line)"
                }}>
                    <div style={cardShadowStyle(seedSides)} />
                    {/* Same top/bottom inset (2px) regardless of seedSides, unlike the
                        shadow card above (needs the asymmetric 0/2 split so its own
                        drawn edge sits flush on the internal/shared side) - this div only
                        needs to stay inside that drawn box, not match it exactly, and 2px
                        both sides always does that safely. Matching insets center the seed
                        input on the row itself instead of 1px off it, same fix as
                        BracketClassic.js's identical div. left/right stay asymmetric -
                        horizontal position, not row-height related. */}
                    <div style={{
                        position: "absolute",
                        top: 2,
                        right: seedSides.right ? 0 : 2,
                        bottom: 2,
                        left: seedSides.left ? 0 : 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        // more left than right padding - shifts the seed value off the
                        // card's left border (the only real, visible edge here; the
                        // right side is flush with the player cell) instead of dead
                        // centering it, which read as cramped against that border for
                        // 2-character seeds ("A1"). Horizontal total is unchanged, so
                        // this doesn't need the cell any wider.
                        padding: is_authorized ? "0 4px 0 8px" : "0 6px 0 14px"
                    }}>
                        {
                            is_authorized ?
                                <input
                                    type="text" maxLength={3}
                                    defaultValue={(r1Match ? (side === 1 ? r1Match.seed1 : r1Match.seed2) : "") || ""}
                                    disabled={!r1Match}
                                    onBlur={r1Match ? e => handleSeedChange(r1Match.id, side, e.target.value) : undefined}
                                    style={{paddingTop: "10px", paddingBottom: "10px", width: "38px", height: "39px", boxSizing: "border-box", textAlign: "center"}}
                                />
                                : <>{(r1Match ? (side === 1 ? r1Match.seed1 : r1Match.seed2) : "")}</>
                        }
                    </div>
                </td>
            );
        }

        const isSlotEditable = isMainDraw && !!r1Match;
        const slotOccupied = isSlotEditable && hasParticipant(r1Match, side);
        // Unlike slotOccupied above (isMainDraw-gated - only meaningful for the
        // drag/pick-up-to-move affordances), placement draws (3-8/9-16/etc) have
        // real round-1 participants too, just not editable ones - this drives the
        // name-click-opens-menu decision below for every draw, not just the main one.
        // hasParticipant alone also passes for a bye (x1/x2 true, no real name) -
        // excluded here too, so tapping a "Bye" slot doesn't pop an empty/broken menu.
        const isBye = side === 1 ? r1Match?.x1 : r1Match?.x2;
        const hasRealParticipant = !!r1Match && !isBye && hasParticipant(r1Match, side);
        const isThisPicked = isSlotEditable && pickedSlot?.matchId === r1Match.id && pickedSlot?.side === side;
        const isThisPickerTarget = isSlotEditable && pickerTarget?.matchId === r1Match.id && pickerTarget?.side === side;
        // loadingSlots is this component's own (drag/move only) - loadingCells
        // is the parent's, set while an add-participant request from the
        // picker modal/candidate-place flow is in flight for this same slot.
        const isSlotLoading = isSlotEditable && !!(loadingSlots[slotKey(r1Match.id, side)] || loadingCells?.[slotKey(r1Match.id, side)]);

        cells.push(
            <td key="player"
                style={{
                    ...(isMainDraw && r1Match ? {
                        cursor: is_authorized
                            ? (isSlotLoading ? "default" : (pickedSlot || pickedCandidate) ? "crosshair" : slotOccupied ? "grab" : "pointer")
                            : "pointer"
                    } : {}),
                    minWidth: is_authorized ? (isMainDraw ? "120px" : "140px") : showSeedColumn ? "150px" : "140px",
                    // round-1 slots can show the group-add-participant button
                    // (39px min-height) when empty - give the row that much
                    // room always, so it doesn't grow for the button and then
                    // shrink back down to round 2+'s plain 44px the moment a
                    // name fills it in. 48px matches TournamentGroupTable.js's
                    // own equivalent cell exactly (same button, same class) -
                    // was 54px, taller than Groups for no reason other than
                    // never having been checked against it.
                    // Keyed on r1Match alone (not isMainDraw && r1Match) - placement
                    // draws (3-8/9-16/etc) have real round-1 matches too, just not
                    // editable ones (see isSlotEditable below, still isMainDraw-gated
                    // for that); this is purely visual sizing and looked visibly
                    // smaller/cramped there than on the main draw for no real reason.
                    height: r1Match ? "48px" : "44px",
                    verticalAlign: "middle",
                    pointerEvents: isSlotLoading ? "none" : undefined,
                    padding: 0,
                    // .bracket-table td (index.css) still sets its own border-bottom -
                    // explicit "none" beats it, same reasoning as the seed cell above.
                    // The shadow card is the only visual boundary now, except for the
                    // real divider between this match's two players (see the seed
                    // cell's own comment on this same property).
                    border: "none",
                    borderBottom: isBottom ? "none" : "1px solid var(--line)"
                }}
                draggable={is_authorized && isSlotEditable && slotOccupied && !isTouchDevice && !pickedSlot && !isSlotLoading && !pickerTarget && !pickedCandidate}
                onDragStart={is_authorized && isSlotEditable && slotOccupied && !isSlotLoading && !pickerTarget && !pickedCandidate ? e => handleDragStart(e, r1Match.id, side) : undefined}
                onDragOver={is_authorized && isSlotEditable && !isSlotLoading ? e => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; } : undefined}
                onDrop={is_authorized && isSlotEditable && !isSlotLoading ? e => handleDrop(e, r1Match.id, side) : undefined}
                onTouchStart={is_authorized && isSlotEditable && slotOccupied && !isSlotLoading && !pickerTarget && !pickedCandidate ? e => handleTouchStart(e, r1Match.id, side) : undefined}
                onContextMenu={is_authorized && isSlotEditable && slotOccupied ? e => e.preventDefault() : undefined}
                onClick={() => {
                    if (!r1Match || isSlotLoading) return;
                    if (pickedSlot) {
                        if (isSlotEditable) handleCellTap(r1Match.id, side);
                        return;
                    }
                    // A candidate was picked from the still-open panel (no
                    // slot targeted yet) - any empty, editable round-1 slot,
                    // including one that would otherwise open the panel or
                    // an occupied slot's actions menu, just places them
                    // here. An occupied/non-editable slot cancels the pick
                    // instead of falling through to those - was a bare
                    // no-op, but the panel can stay open across several
                    // picks, so clicking through a few candidates while
                    // just browsing left this stuck on and every click on
                    // an occupied slot silently did nothing.
                    if (pickedCandidate) {
                        if (isSlotEditable && !slotOccupied) onPlaceCandidate(r1Match.id, side);
                        else onCancelCandidate?.();
                        return;
                    }
                    // Click on an occupied slot always opens the actions menu
                    // (profile/delete, plus "Move" when it's an editable
                    // main-draw round-1 slot - see openParticipantMenu's
                    // allowMove param), same as Groups' own participant
                    // click - consistent everywhere, touch or desktop.
                    // Long-press (handleTouchStart above) is the separate,
                    // dedicated gesture for picking a slot straight up to
                    // move it. isBye too (openParticipantMenu handles it,
                    // showing just Delete/Move) - excluding it here just
                    // routed the click to handlePlayerClick's add-participant
                    // picker instead, which has no remove/delete of its own,
                    // leaving Bye slots with no way to clear them at all.
                    if ((hasRealParticipant || isBye) && is_authorized) {
                        openParticipantMenu(r1Match, side, { allowMove: isSlotEditable && slotOccupied });
                        return;
                    }
                    if (is_authorized) {
                        handlePlayerClick(r1Match, side);
                        return;
                    }
                    handleMatchClick(r1Match.match_number);
                }}
            >
                {/* See cardShadowStyle above - "left" is internal only when a seed
                    cell is merged in on this side too (showSeedColumn). */}
                <div style={cardShadowStyle({ top: isBottom, right: false, bottom: !isBottom, left: showSeedColumn })} />
                {/* padding matches BracketClassic.js's own player cell wrapper -
                    was a flat "5px 10px" here vs Classic's isSlotEditable-based
                    "0 5px"/"5px" split, a small but visible spacing mismatch
                    between the two views for the same kind of cell. The extra
                    left padding (13px, not 5px) mirrors the 8px of horizontal
                    padding Classic's own renderSide wrap() div always adds on
                    top of this same wrapper - Pro's wrap() (below) is a bare
                    unpadded span, so without this the name sat visibly closer
                    to the left edge than in Classic. Unconditional (not just
                    when a seed column sits to the left) since round-1 cards on
                    the placement draws (3-8/9-16/etc, no seed column there)
                    have a real bordered left edge of their own to clear too -
                    same reasoning Classic's wrap() already applies everywhere,
                    seed column or not. */}
                {/* r1Match, not isSlotEditable - same reasoning as the height comment
                    above, this is sizing/spacing only, not the actual edit gating. */}
                <div style={{ position: "relative", height: "100%", boxSizing: "border-box", padding: r1Match ? "0 5px 0 13px" : "5px", display: "flex", alignItems: "center" }}>
                    {isSlotLoading
                        ? <span className="player cursor" style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}><span className="loading-dots"><span></span><span></span><span></span></span></span>
                        : (pickedSlot || pickedCandidate) && isSlotEditable && !slotOccupied
                        ? <span className="player cursor group-add-participant bracket-drop-here">{t("Drop here")}</span>
                        // dim just the participant, not the .picked td (which also carries the
                        // cell's own backgroundColor - applying opacity there faded that
                        // background in too, reading as a separate highlighted box)
                        : <span style={{ opacity: isThisPicked ? 0.5 : 1 }}>
                            {r1Match ? renderSide(r1Match, side, isMainDraw, i + 1, isThisPickerTarget) : (i < draw.number_players ? `Player ${i + 1}` : 'Bye')}
                        </span>}
                </div>
            </td>
        );

        for (let r = 1; r <= R; r++) {
            const blockSize = 1 << r;
            const k = i >> r;
            const blockStart = k * blockSize;
            const mRow = blockStart + (blockSize >> 1) - 1;
            const matchNum  = matchStart(r) + k + 1;
            const span      = Math.max(2, 1 << (r - 1));
            const startRow  = mRow - (span >> 1) + 1;
            const inSpan    = i >= startRow && i < startRow + span;
            // #N label and score cells have no dimmed/highlighted content of
            // their own (unlike the name cells above, via renderSide's own
            // wrap()) - without this class they stayed full-opacity even
            // when the rest of this match's row faded out, so the match read
            // as only half-dimmed instead of as one block.
            const rowMatchDim = searchStateClass(matchSearchState(matchByNum[matchNum]));

            if (i === startRow) {
                cells.push(
                    <td key={`m${r}`} rowSpan={getRs(startRow, span / 2)}
                        className={`no_border match_number ${r !== 1 ? "border-left" : ""} ${rowMatchDim}`}
                        style={{
                            cursor: "pointer",
                            textAlign: "left",
                            verticalAlign: "bottom",
                            borderBottom: "1px solid var(--line)",
                            left: "-5px"
                        }}
                        onClick={() => handleMatchClick(matchNum)}
                    >
                        <label>#{matchNum}</label>
                    </td>
                );
            } else if (i === mRow + 1) {
                cells.push(
                    <td key={`m_bot${r}`} rowSpan={getRs(mRow + 1, span / 2)}
                        className={`no_border match_number ${r !== 1 ? "border-left" : ""} ${rowMatchDim}`}
                        style={{ cursor: "pointer", width: "30px" }}
                        onClick={() => handleMatchClick(matchNum)}
                    ></td>
                );
            }

            if (i === mRow) {
                const currentMatch = matchByNum[matchNum];
                const nextMatchNum = r < R ? matchStart(r + 1) + (k >> 1) + 1 : null;
                const nextMatch    = nextMatchNum ? matchByNum[nextMatchNum] : null;
                const nextSide     = (k & 1) === 0 ? 1 : 2;
                let display = <span>{t("Winner")} #{matchNum}</span>;
                if (nextMatch) {
                    display = renderSide(nextMatch, nextSide);
                } else if (currentMatch) {
                    if (currentMatch.winner_side) {
                        display = renderSide(currentMatch, currentMatch.winner_side);
                    } else if (currentMatch.x1 && currentMatch.x2) {
                        display = <span>{t("Bye")}</span>;
                    } else if (currentMatch.x1) {
                        display = renderSide(currentMatch, 2);
                    } else if (currentMatch.x2) {
                        display = renderSide(currentMatch, 1);
                    }
                }
                // Клик здесь всегда открывает сам матч, для любого раунда и любого
                // зрителя - меню участника (профиль/удаление) остаётся только на
                // 1 раунде основной сетки (см. "player" ячейку выше), где
                // участников реально добавляют/удаляют.
                cells.push(
                    <td
                        key={`w${r}`}
                        className="no-bottom"
                        rowSpan={getRs(i, 1)}
                        onClick={() => handleMatchClick(matchNum)}
                        style={{cursor: "pointer"}}
                    >
                        {display}
                    </td>
                );
            } else if (i === mRow + 1) {
                const match = matchByNum[matchNum];
                const hasScores = match?.sets?.some(s => s.game_score_1 != null || s.game_score_2 != null || s.tiebreak_score_1 != null || s.tiebreak_score_2 != null);
                cells.push(
                    <td
                        key={`sc${r}`}
                        className={`no_border no-top ${rowMatchDim}`}
                        rowSpan={getRs(i, 1)}
                        onClick={() => handleMatchClick(matchNum)}
                        style={{cursor: "pointer"}}
                    >
                        {match && hasScores ? <span className="matches-points">{renderScore(match)}</span> : ""}
                    </td>
                );
            } else if (inSpan) {
                cells.push(<td key={`ew${r}`} rowSpan={getRs(i, 1)} style={{ border: 'none' }} />);
            } else {
                cells.push(<td key={`em${r}`} rowSpan={getRs(i, 1)} style={{ border: 'none' }} />);
                cells.push(<td key={`ew${r}`} rowSpan={getRs(i, 1)} style={{ border: 'none' }} />);
            }
        }

        const finalMRow = (1 << (R - 1)) - 1;
        cells.push(<td key="spacer" rowSpan={getRs(i, 1)} style={{ border: 'none', borderBottom: i === finalMRow ? "1px solid var(--line, #ccc)" : "none" }}></td>);
        const finalCells = i === 0 ? cells.map(c => React.cloneElement(c, { style: { ...c.props.style } })) : cells;
        
        const rows = [<tr key={i}>{finalCells}</tr>];
        if (isBottom && i !== N - 1) {
            const leftColsCount = 1 + (showSeedColumn ? 1 : 0) + 1;
            rows.push(
                <tr key={`gap_${i}`} className="bracket-gap-row">
                    <td colSpan={leftColsCount} style={{ height: "4px", border: "none", padding: 0 }}></td>
                </tr>
            );
        }
        return rows;
    }).flat(), [N, R, round1Matches, matchByNum, isMainDraw, matchStart, handleSeedChange,
        handlePlayerClick, handleMatchClick, renderSide, hasParticipant, openParticipantMenu,
        draw.number_players, showSeedColumn, is_authorized,
        pickedSlot, pickerTarget, pickedCandidate, onPlaceCandidate, isTouchDevice, handleDragStart, handleDrop, handleTouchStart, handleCellTap, loadingSlots, loadingCells, matchSearchState]);

    return (
        <div style={{ position: "relative" }}>
            {pickedSlot && (
                <div
                    onClick={() => setPickedSlot(null)}
                    style={{
                        position: "fixed",
                        bottom: "20px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        backgroundColor: "#111827",
                        color: "white",
                        padding: "10px 20px",
                        borderRadius: "20px",
                        zIndex: 100,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        cursor: "pointer",
                        userSelect: "none"
                    }}
                >
                    <span style={{ fontSize: "14px", fontWeight: "500", whiteSpace: "nowrap" }}>{t("Tap destination cell")}</span>
                    <FeatherIcon icon="x" size={16} />
                </div>
            )}
            {/* Same bottom pill as pickedSlot above (moving an already-seated
                participant) - pickedCandidate is the equivalent "in hand"
                state for a brand new one picked from the picker modal/panel,
                but had no visible cancel affordance of its own: onCancelCandidate
                was wired in (see the slot click handler above) yet nothing
                ever rendered a control that actually called it. */}
            {pickedCandidate && (
                <div
                    onClick={() => onCancelCandidate?.()}
                    style={{
                        position: "fixed",
                        bottom: "20px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        backgroundColor: "#111827",
                        color: "white",
                        padding: "10px 20px",
                        borderRadius: "20px",
                        zIndex: 100,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        cursor: "pointer",
                        userSelect: "none"
                    }}
                >
                    <span style={{ fontSize: "14px", fontWeight: "500", whiteSpace: "nowrap" }}>{t("Tap destination cell")}</span>
                    <FeatherIcon icon="x" size={16} />
                </div>
            )}
            {!isMainDraw && is_authorized && (
                <div
                    className="delete-team"
                    style={{ zIndex: 2, position: "absolute", top: "12px", right: "15px", cursor: "pointer" }}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(t("Are you sure you want to delete this bracket?"))) {
                            api.delete(`/api/v1.0/tournaments/${id}/draw-stage/draws/${draw.id}`)
                                .then(() => toast.success(t("Bracket deleted")))
                                .catch((error) => {
                                    console.error(error);
                                    toast.error(t("Failed to delete the bracket"));
                                });
                        }
                    }}
                >
                    <FeatherIcon icon="x" size={14} />
                </div>
            )}
            <div
                className={`bracket-table ${(canEdit && is_authorized) ? "" : "small_view"}`}
                style={{ marginTop: "10px", minHeight: "auto", overflowY: "hidden" }}
            >
                <table style={{ width: "100%" }}>
                    <thead>
                        <tr>
                            <th colSpan={100} className={is_authorized ? "authorized-header" : ""}>
                                <div style={{ display: "flex", justifyContent: "flex-start",  marginLeft: "-30px", color: is_authorized ? undefined : "#fff"
                                }}>
                                    <span className="bracket-sticky-left">{t("Bracket of")} {draw.number} {t("place")}</span>
                                </div>
                            </th>
                        </tr>
                        <tr className="group-header">{headerCells}</tr>
                    </thead>
                    <tbody>
                    {bodyRows}
                    <tr><td colSpan={100} style={{ height: "25px", padding: 0, border: "none" }}></td></tr>
                    </tbody>
                </table>
            </div>
            <ParticipantActionsModal
                isOpen={!!actionsModal}
                onClose={() => setActionsModal(null)}
                title={actionsModal?.title}
                actions={actionsModal?.actions || []}
            />
        </div>
    );
}

// memo: this renders once per draw ("All brackets" can mount several at
// once), and its own parent (TournamentGrids.js) re-renders on state that
// has nothing to do with any one draw (search chips, the add-participant
// modal opening, wide-viewport layout changes...) - without this, every one
// of those re-rendered every mounted bracket's whole table too. Only pays
// off because TournamentGrids.js also stabilized (useCallback) every
// function prop passed here - an unmemoized handler recreated fresh each
// render would fail the shallow prop comparison below every time regardless.
export default React.memo(Bracket);
